import {
  ACCT_INACCESSIBLE,
  AUTH_ERROR,
  AUTH_INVALID,
  CARD_ADD_FAILED,
  FRAUD,
  UNKNOWN_ERROR
} from '../constants/api-error-enums';
import { INVALID_GRANT } from '../constants/common-enums';
import handleGenericError from './convert-api-error';
import getLockOutFlowForB2CIAMApis from './get-lock-out-flow-for-b2ciam-apis';
import isEmptyString from './is-empty-string';
import isFraud from './is-fraud';
import handleLockOutError from './is-locked';

const DEFAULT_ERROR_MODEL = {
  details: [],
  message: '',
  reason: ''
};
/**
 * ConvertApiErrorClass used to map backend error response to enums
 */
class ConvertApiError {
  /**
   * Default constructor
   */
  constructor() {
    this.error = { ...DEFAULT_ERROR_MODEL };
  }
  /**
   * clear error before each error handling
   */
  clearError() {
    this.error = { ...DEFAULT_ERROR_MODEL };
  }
  /**
   * handle account/device lock error
   * @param {object} response
   */
  handleFraud(response) {
    this.error.reason = isFraud(response) ? FRAUD : '';
  }
  /**
   * handle standard/generic error
   * @param {object} response
   * @return {object} generic error object
   */
  handleGenericError(response) {
    this.error.reason = handleGenericError(response);
    return this.error;
  }
  /**
   * handle account/device lock error
   * @param {object} response
   * @param {object} isIAMAPI - boolean indicating if this is being called for an IAM API
   */
  handleLockOutError(response, isIAMAPI = false) {
    this.clearError();
    // F21350 - lockout must be determined a different way for IAM (Identtity and Access Management) APIs
    const lockoutStatus = isIAMAPI
      ? getLockOutFlowForB2CIAMApis(response)
      : handleLockOutError(response);
    if (lockoutStatus) {
      this.error.reason = ACCT_INACCESSIBLE;
    }
  }
  /**
   * handle identityLookup error
   * @param {object} response
   * @return {object} error object
   */
  handleIdentityLookupError(response) {
    this.clearError();
    this.handleFraud(response);
    if (isEmptyString(this.error.reason)) {
      this.handleLockOutError(response, true);
    }
    if (isEmptyString(this.error.reason)) {
      this.handleGenericError(response);
    }
    return this.error;
  }
  /**
   * handle getSrcProfile error
   * @param {object} response
   * @return {object} error object
   */
  handleGetSrcProfileError(response) {
    this.clearError();
    if (response.httpStatus === 400 && response.errorCode === INVALID_GRANT) {
      this.error.reason = AUTH_INVALID;
    } else {
      this.handleGenericError(response);
    }
    return this.error;
  }

  /**
   * handle pi error
   * @param {object} response
   * @return {object} error object
   */
  handlePaymentInstrumentError(response) {
    this.clearError();
    // 412 errorCode unique to pi api, per https://visawiki.trusted.visa.com/pages/viewpage.action?pageId=297137008
    if (response.httpStatus === 412) {
      this.error.reason = CARD_ADD_FAILED;
    } else {
      this.handleGenericError(response);
    }
    return this.error;
  }
  /**
   * handle intent error
   * @param {object} response
   * @return {object} error object
   */
  handleGetIntentError(response) {
    this.clearError();
    this.handleGenericError(response);
    return this.error;
  }

  handlePutIntentError(response) {
    this.clearError();
    // 403 for PUT intent is AUTH_ERROR
    if (response.httpStatus === 403) {
      this.error.reason = AUTH_ERROR;
    } else {
      this.handleGenericError(response);
    }
    return this.error;
  }

  handleFetchNetworkError() {
    this.clearError();
    this.error.reason = UNKNOWN_ERROR;
    return this.error;
  }
}

export default new ConvertApiError();
